/* ============================================================
   Nutry — storefront runtime (v2).
   All page data is injected by Liquid into window.NUTRY, so this
   file never hard-codes a product, price or colour.
   v2: the homepage hero and sticky bar moved to nutry-v2.js; the
   cart now speaks in packs (one pack size) and only merchandises
   products that are live.
   ============================================================ */
(() => {
  "use strict";
  const $  = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const D  = window.NUTRY || {};
  const SKUS = D.skus || [];
  const LIVE = SKUS.filter(s => s.live);
  const FREE_AT = D.freeAt || 0;          // cents
  const SHIP    = D.ship   || 0;          // cents
  const money = c => (c / 100).toLocaleString("en-US", { maximumFractionDigits: 0 }) + " EGP";
  /* pack ladder, mirroring the store's automatic discounts so the optimistic cart total never
     differs from what Shopify sends back: 2+ packs = a percentage off, 3+ packs = a fixed amount
     off the order. Shopify applies whichever is better for the customer, so we do the same. */
  const LADDER = D.ladder || { 2: 10 };
  const OFF3 = D.off3 || 0;                // cents
  const OFF2 = D.off2 || 0;                // cents — the honey ladder is a fixed amount off at 2 packs, not a percentage
  const linePrice = (unit, qty) => {
    const list = unit * qty;
    const byPct = qty >= 2 ? (OFF2 ? list - OFF2 : Math.round(list * (100 - (LADDER[2] || 0)) / 100)) : list;
    const byAmt = qty >= 3 && OFF3 ? list - OFF3 : list;
    return Math.min(byPct, byAmt);
  };

  /* ==========================================================
     1. Ticker duplication (seamless loop needs 2 copies)
     ========================================================== */
  const tick = $("#ticker");
  if (tick && D.ticker) {
    const run = D.ticker.map(f =>
      `<span class="t-item">${f}</span><i class="t-dot" aria-hidden="true"></i>`).join("");
    /* Just enough copies to cover twice the screen (always an even number, animated by half the
       track), and an explicit pixel width once the fonts are in. Two reasons: Safari mis-measures
       max-content on nested rows, so -50% was not one clean lap; and a track far wider than the
       screen is a huge compositor layer that iOS drops under memory pressure, which is when the
       strip froze and went blank on phones. */
    const build = () => {
      tick.style.width = "";
      tick.innerHTML = `<span>${run}</span>`;
      const one = tick.firstElementChild.getBoundingClientRect().width || 1;
      const n = Math.max(2, 2 * Math.ceil(innerWidth / one));
      let html = "";
      for (let i = 0; i < n; i++) html += `<span${i ? ' aria-hidden="true"' : ""}>${run}</span>`;
      tick.innerHTML = html;
      tick.style.width = Math.ceil(one * n) + "px";
    };
    build();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(build);
    /* rebuild only when the width really changes: iOS fires resize as its address bar collapses on
       every scroll, and a rebuild restarts the strip from the beginning */
    let rt, lastW = innerWidth;
    addEventListener("resize", () => {
      if (innerWidth === lastW) return;
      lastW = innerWidth;
      clearTimeout(rt); rt = setTimeout(build, 150);
    }, { passive: true });
  }

  /* ==========================================================
     2. Drawers — menu (left) and cart (right)
     ========================================================== */
  function drawer(panelSel, scrimSel, btnSel, closeSel, onOpen) {
    const panel = $(panelSel), scrim = $(scrimSel), btn = $(btnSel), x = $(closeSel);
    if (!panel || !scrim) return { open() {}, close() {} };
    let last = null;
    const open = () => {
      last = document.activeElement;
      scrim.hidden = false;
      requestAnimationFrame(() => { scrim.classList.add("on"); panel.classList.add("on"); });
      panel.setAttribute("aria-hidden", "false");
      panel.removeAttribute("inert");
      document.body.style.overflow = "hidden";
      document.body.classList.add("drawer-open");
      if (btn) btn.classList.add("on");
      if (x) x.focus();
      if (onOpen) onOpen();
    };
    const close = () => {
      scrim.classList.remove("on"); panel.classList.remove("on");
      panel.setAttribute("aria-hidden", "true");
      panel.setAttribute("inert", "");
      document.body.style.overflow = "";
      document.body.classList.remove("drawer-open");
      if (btn) btn.classList.remove("on");
      setTimeout(() => { scrim.hidden = true; }, 420);
      if (last) last.focus();
    };
    if (btn) btn.addEventListener("click", () => panel.classList.contains("on") ? close() : open());
    if (x) x.addEventListener("click", close);
    scrim.addEventListener("click", close);
    addEventListener("keydown", e => { if (e.key === "Escape" && panel.classList.contains("on")) close(); });
    return { open, close };
  }

  const menu = drawer("#menu", "#menuScrim", "#menuBtn", "#menuX");
  $$("#menu a:not([data-notify])").forEach(a => a.addEventListener("click", () => menu.close()));

  /* ==========================================================
     3. Cart — Shopify AJAX Cart API, not local state
     ========================================================== */
  const cartPanel = $("#cart");
  const cart = drawer("#cart", "#cartScrim", "#cartBtn", "#cartX");
  let CART = null;

  /* Everything needed to render a cart line instantly, keyed by variant id. */
  const VMAP = {};
  SKUS.forEach(s => {
    if (s.v) VMAP[s.v] = { title: s.packLabel, product: s.title, price: s.price, pid: s.productId, accent: s.accent };
  });
  const PD = window.NUTRY_PDP;
  if (PD && PD.skus) {
    Object.values(PD.skus).forEach(sk => {
      (sk.variants || []).forEach(v => {
        VMAP[v.id] = { title: v.title, product: sk.title, price: v.price, pid: sk.productId || null, accent: sk.accent };
      });
    });
  }

  const post = (url, body) => fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body)
  }).then(r => r.json());

  function refresh() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(r => r.json())
      .then(c => { CART = c; render(); return c; })
      .catch(() => {});
  }

  const LOCK = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4.5" y="10.5" width="15" height="10" rx="2.5"/><path d="M8 10.5V7.8a4 4 0 0 1 8 0v2.7"/><circle cx="12" cy="15.5" r="1.2" fill="currentColor" stroke="none"/></svg>`;
  const TICK = `<span class="cd2-tick" aria-hidden="true"><svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 6.5l2.5 2.5 4.5-5"/></svg></span>`;
  const TRUST = `<p class="cart-trust">cash on delivery · 2–3 days · 14-day returns</p>`;
  const packImg = s => s && s.pack ? `<img src="${s.pack}" alt="" loading="lazy">` : "";

  function render() {
    if (!CART || !cartPanel) return;
    const n = CART.item_count, sub = CART.total_price;

    const badge = $("#cartN");
    if (badge) { badge.textContent = n; badge.classList.toggle("on", n > 0); }
    const title = $("#cartTitle");
    if (title) title.innerHTML = n ? `Your cart · <b>${n}</b>` : "Your cart";

    /* shipping bar: distance to free delivery, then the unlock. The text is swapped in place
       (never blanked) and the bar eases to its new length. */
    const ship = $("#cartShip");
    if (ship) {
      if (!n || !FREE_AT) ship.style.display = "none";
      else {
        ship.style.display = "";
        const left = FREE_AT - sub, done = left <= 0;
        ship.classList.toggle("done", done);
        let p = ship.querySelector(".cd2-ship-t"), bar = ship.querySelector(".cd2-bar i");
        if (!p) {
          ship.innerHTML = `<p class="cd2-ship-t"></p><div class="cd2-bar"><i></i></div>`;
          p = ship.querySelector(".cd2-ship-t"); bar = ship.querySelector(".cd2-bar i");
        }
        let html = done ? `${TICK}<b>Free delivery</b> unlocked` : `<em>${money(left)}</em> away from free delivery`;
        if (D.range && LIVE.length > 1) {
          html = n === 1 ? `Add any second pack: <b>${LADDER[2] || 0}% off</b> and free delivery`
               : n === 2 ? `Add any third pack: <b>${money(OFF3)} off</b> the order`
               : `${TICK}<b>${n} packs</b> · free delivery`;
        }
        if (p.innerHTML !== html) { p.innerHTML = html; p.style.animation = "none"; void p.offsetWidth; p.style.animation = ""; }
        requestAnimationFrame(() => { bar.style.width = Math.min(100, sub / FREE_AT * 100) + "%"; });
      }
    }

    const body = $("#cartBody");
    if (body) {
      if (!n) {
        body.innerHTML = `<div class="cd2-empty">
            <p class="v2-disp cd2-h">your cart is empty.</p>
            <p>One sachet a day. Start with a pack.</p>
            ${LIVE.map(s => `<button class="cd2-qa" type="button" data-add="${s.v}">
              <span class="cd2-img">${packImg(s)}</span>
              <span><span class="cd2-qa-n">${s.title}</span><span class="cd2-qa-s v2-mono">12 sachets<br>${money(s.price)}</span></span>
              <span class="cd2-qa-add">Add <span>→</span></span>
            </button>`).join("")}
          </div>`;
      } else {
        body.innerHTML = CART.items.map(i => {
          const s = SKUS.find(x => x.productId === i.product_id);
          const vid = i.variant_id || i.id, q = i.quantity, unit = i.price || 0;
          const list = i.original_line_price != null ? i.original_line_price : unit * q;
          const save = list - (i.line_price || 0);
          let up = "";
          /* one line only: offer the next pack of the same product */
          if (s && s.live && CART.items.length === 1 && q < 3 && !(D.range && LIVE.length > 1)) {
            const nq = q + 1, more = linePrice(unit, nq) - linePrice(unit, q);
            const why = nq === 2
              ? (linePrice(unit, 2) >= FREE_AT ? "free delivery" : "save " + (OFF2 ? money(OFF2) : (LADDER[2] || 0) + "%"))
              : "instead of " + money(unit).replace(" EGP", "");
            up = `<div class="cd2-up">
                <div><b>Add a ${nq === 2 ? "2nd" : "3rd"} pack</b><small class="v2-mono"><em>${money(more)}</em> · ${why}</small></div>
                <button type="button" data-vid="${vid}" data-q="${nq}">Add <span>+</span></button>
              </div>`;
          }
          return `<div class="cd2-ci">
            <span class="cd2-img">${packImg(s)}</span>
            <div>
              <div class="cd2-top">
                <div><div class="cd2-name">${i.product_title}</div><div class="cd2-sub v2-mono">${q} pack${q > 1 ? "s" : ""} · ${q * 12} sachets</div></div>
                <div class="cd2-price">${money(i.line_price)}${save > 0 ? `<s>${money(list)}</s>` : ""}</div>
              </div>
              <div class="cd2-row">
                <span class="cd2-qty" role="group" aria-label="Quantity">
                  <button type="button" data-vid="${vid}" data-q="${q - 1}" aria-label="Decrease">−</button><span>${q}</span><button type="button" data-vid="${vid}" data-q="${q + 1}" aria-label="Increase">+</button>
                </span>
                <button class="cd2-rm v2-mono" type="button" data-vid="${vid}" data-q="0">Remove</button>
              </div>
            </div>
          </div>${up}`;
        }).join("");
        /* the ladder counts packs across every product: offer the other live products until 3 packs are in */
        const inCart = new Set(CART.items.map(i => i.product_id));
        const others = LIVE.filter(x => !inCart.has(x.productId));
        if (D.range && LIVE.length > 1 && n < 3 && others.length) {
          const why = n === 1 ? `<em>${LADDER[2] || 0}% off</em> · free delivery` : `<em>${money(OFF3)} off</em> the order`;
          body.innerHTML += `<div class="cd2-up cd2-up--range">
              <div><b>Add another product</b><small class="v2-mono">${why}</small></div>
              <span class="cd2-up-row">${others.map(x => `<button type="button" data-add="${x.v}" style="--c:${x.tint || x.accent}"><i></i>${x.key} <span>+</span></button>`).join("")}</span>
            </div>`;
        }
      }
    }

    const foot = $("#cartFoot");
    if (foot) {
      if (!n) {
        foot.innerHTML = `<span class="cd2-go off" aria-disabled="true">${LOCK}<span>Secure checkout</span></span>${TRUST}`;
      } else {
        const s2 = sub >= FREE_AT ? 0 : SHIP;
        foot.innerHTML = `
          <div class="sum"><span>Subtotal</span><b>${money(sub)}</b></div>
          <div class="sum"><span>Delivery</span><b class="${s2 ? "" : "free"}">${s2 ? money(s2) : "Free"}</b></div>
          <div class="sum sum--tot"><span>Total</span><b>${money(sub + s2)}</b></div>
          <a class="cd2-go" href="/checkout">${LOCK}<span>Secure checkout</span></a>${TRUST}`;
      }
    }
  }

  function add(variantId, qty) {
    const v = Number(variantId), q = qty || 1;
    if (!CART) CART = { item_count: 0, total_price: 0, items: [] };
    const info = VMAP[v];
    let line = CART.items.find(i => (i.variant_id || i.id) === v);
    if (line) {
      line.quantity += q;
      line.line_price = linePrice(line.price || (info && info.price) || 0, line.quantity);
      line.original_line_price = (line.price || (info && info.price) || 0) * line.quantity;
    } else {
      CART.items.push({ id: v, variant_id: v, product_id: info && info.pid,
        product_title: info ? info.product : "Added",
        variant_title: info ? info.title : "",
        quantity: q, price: info ? info.price : 0,
        line_price: linePrice(info ? info.price : 0, q),
        original_line_price: (info ? info.price : 0) * q });
    }
    CART.item_count += q;
    CART.total_price = CART.items.reduce((t, i) => t + (i.line_price || 0), 0);
    render();
    cart.open();
    return post("/cart/add.js", { items: [{ id: v, quantity: q }] })
      .then(() => refresh())
      .catch(() => refresh());
  }
  window.nutryAdd = add;
  /* several variants at once (the set): optimistic lines for each, then one add.js call */
  function addMany(ids) {
    if (!CART) CART = { item_count: 0, total_price: 0, items: [] };
    ids.forEach(id => {
      const v = Number(id), info = VMAP[v];
      let line = CART.items.find(i => (i.variant_id || i.id) === v);
      if (line) { line.quantity += 1; line.line_price = linePrice(line.price || 0, line.quantity); line.original_line_price = (line.price || 0) * line.quantity; }
      else CART.items.push({ id: v, variant_id: v, product_id: info && info.pid, product_title: info ? info.product : "Added", variant_title: info ? info.title : "", quantity: 1, price: info ? info.price : 0, line_price: info ? info.price : 0, original_line_price: info ? info.price : 0 });
      CART.item_count += 1;
    });
    CART.total_price = CART.items.reduce((t, i) => t + (i.line_price || 0), 0);
    render();
    cart.open();
    return post("/cart/add.js", { items: ids.map(id => ({ id: Number(id), quantity: 1 })) })
      .then(() => refresh())
      .catch(() => refresh());
  }
  window.nutryAddMany = addMany;

  document.addEventListener("click", e => {
    const q = e.target.closest("[data-vid]");
    if (q) {
      const vid = Number(q.dataset.vid), nq = Number(q.dataset.q);
      const it = CART && CART.items.find(i => (i.variant_id || i.id) === vid);
      if (it) {
        CART.item_count += nq - it.quantity;
        it.quantity = nq;
        it.line_price = linePrice(it.price || 0, nq);
        it.original_line_price = (it.price || 0) * nq;
        if (nq < 1) CART.items = CART.items.filter(x => x !== it);
        CART.total_price = CART.items.reduce((t, i) => t + (i.line_price || 0), 0);
        render();
      }
      post("/cart/change.js", { id: String(vid), quantity: nq })
        .then(c => { CART = c; render(); })
        .catch(() => refresh());
      return;
    }
    const st = e.target.closest("[data-add-set]");
    if (st) {
      e.preventDefault();
      addMany(st.dataset.addSet.split(",").filter(Boolean));
      st.classList.add("done"); setTimeout(() => st.classList.remove("done"), 1600);
      return;
    }
    const a = e.target.closest("[data-add]");
    if (a) {
      e.preventDefault(); add(a.dataset.add, 1);
      if (a.classList.contains("rg3-add")) { a.classList.add("done"); setTimeout(() => a.classList.remove("done"), 1600); }
    }
  });

  /* ==========================================================
     4. Cutout images: add the shadow only once decoded
     ========================================================== */
  function reveal(root) {
    $$(".imgcut", root || document).forEach(img => {
      const show = () => Promise.resolve(img.decode ? img.decode() : null)
        .catch(() => {})
        .then(() => img.classList.add("imgok"));
      if (img.complete && img.naturalWidth) show();
      else img.addEventListener("load", show, { once: true });
    });
  }
  window.nutryReveal = reveal;
  reveal();

  refresh();
})();
